import React, { useEffect, useMemo, useState } from 'react';
import * as S from './CheckoutRoomChargeAlert.styled';
import Attention from '@mui/icons-material/ReportGmailerrorredSharp';
import MeetingRoom from '@mui/icons-material/MeetingRoom';
import Person from '@mui/icons-material/PersonOutline';
import AccountTree from '@mui/icons-material/AccountTree';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Payments from '@mui/icons-material/Payments';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  bumpRightBarDismiss,
  selectCurrency,
  selectRightBarDismissTick,
  setLayoutRoomChargePanel,
} from 'pages/redux';
import { selectRooms } from 'pages/Hospitality/redux';
import { selectLiveView, selectPermission } from 'redux/global';
import { Room, RoomCharge } from 'types';
import { usePath } from 'pages/hooks';
import { useHistory } from 'react-router-dom';
import { HOSPITALITY_PATHS } from 'pages/Layout/hospitalityModulePaths';
import { canHandleRoomCharge } from 'pages/Settings/redux';
import { logButtonClick } from 'utils/analytics';
import LayoutAlertPresence from './LayoutAlertPresence';

const TWENTY_MIN_MS = 20 * 60 * 1000;
/**
 * Session-only mock settle — hides checkout charge alerts for this sign-in tab/session.
 * Cleared on logout / new browser session so the charge (and alert) return.
 * Legacy localStorage key is cleared on read so old live dismissals do not stick.
 */
const SESSION_DISMISS_KEY = 'session_dismissed_room_charges';
const LEGACY_LIVE_DISMISS_KEY = 'live_dismissed_room_charges';

export type CheckoutChargeAlert = {
  roomId: string,
  roomName: string,
  bookingId: string,
  bookerName: string,
  endDateMs: number,
  amount: number,
  branch?: string,
  description?: string,
};

const getOutstandingCharges = (room: Room): RoomCharge[] => (
  (room.roomCharge || []).filter((c) => String(c.amount ?? '') !== '')
);

const readDismissedIds = (): Set<string> => {
  try {
    // Drop legacy persistent dismissals — mock settle must not survive sessions
    localStorage.removeItem(LEGACY_LIVE_DISMISS_KEY);
    const raw = sessionStorage.getItem(SESSION_DISMISS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

/** Session mock dismissals — Navigation uses these to free the top strip again. */
export const readLiveDismissedRoomChargeIds = readDismissedIds;

const writeDismissedIds = (ids: Set<string>) => {
  sessionStorage.setItem(SESSION_DISMISS_KEY, JSON.stringify([...ids]));
};

/** Clear session mock settles (call on logout so next sign-in sees charges again). */
export const clearSessionDismissedRoomCharges = () => {
  try {
    sessionStorage.removeItem(SESSION_DISMISS_KEY);
    localStorage.removeItem(LEGACY_LIVE_DISMISS_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Mock-settle a booking's room-charge alert for this sign-in session only.
 * Used by live/demo pay flow — does not mutate shared showcase data.
 */
export const mockDismissRoomChargeForSession = (bookingId: string) => {
  const next = new Set(readDismissedIds());
  next.add(String(bookingId));
  writeDismissedIds(next);
  return next;
};

export const collectCheckoutChargeAlerts = (
  rooms: Room[] | null,
  nowMs: number,
  dismissedIds?: Set<string>,
): CheckoutChargeAlert[] => {
  const horizon = nowMs + TWENTY_MIN_MS;
  const alerts: CheckoutChargeAlert[] = [];

  (rooms || []).forEach((room) => {
    const charges = getOutstandingCharges(room);
    if (!charges.length) return;

    const byBooking = new Map<string, RoomCharge[]>();
    charges.forEach((charge) => {
      const key = charge.bookingId || charge.id;
      byBooking.set(key, [...(byBooking.get(key) || []), charge]);
    });

    byBooking.forEach((bookingCharges, bookingId) => {
      if (dismissedIds?.has(String(bookingId))) return;
      const book = (room.books || []).find((b) => b.id === bookingId);
      const endDateMs = book ? +new Date(book.endDate) : 0;
      if (endDateMs > horizon) return;

      const amount = bookingCharges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      alerts.push({
        roomId: room.id,
        roomName: room.name,
        bookingId,
        bookerName: bookingCharges[0]?.bookerName || book?.name || 'Guest',
        endDateMs,
        amount,
        branch: bookingCharges[0]?.branch,
        description: bookingCharges.find((c) => c.description)?.description,
      });
    });
  });

  return alerts.sort((a, b) => a.endDateMs - b.endDateMs);
};

/** True when the checkout room-charge strip is eligible — try-live only. */
export const isCheckoutRoomChargeAlertActive = (
  rooms: Room[] | null,
  permission: number | null | undefined,
  isHomePath: boolean,
  nowMs = Date.now(),
  dismissedIds?: Set<string>,
  liveView = false,
): boolean => (
  // Real hotels settle charges in Reservations — strip is Summit Lodge try-live only
  Boolean(liveView)
  && !isHomePath
  && canHandleRoomCharge(permission)
  && collectCheckoutChargeAlerts(rooms, nowMs, dismissedIds).length > 0
);

const formatDueLabel = (endDateMs: number, nowMs: number) => {
  if (endDateMs <= nowMs) return 'Due / overdue';
  const mins = Math.max(1, Math.ceil((endDateMs - nowMs) / 60000));
  return `In ${mins} min`;
};

const truncateText = (text: string, max = 36) => {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max).trimEnd()}…`;
};

const CheckoutRoomChargeAlert = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const rooms = useAppSelector(selectRooms);
  const permission = useAppSelector(selectPermission);
  const liveView = useAppSelector(selectLiveView);
  const currency = useAppSelector(selectCurrency);
  const rightBarDismissTick = useAppSelector(selectRightBarDismissTick);
  const { isHomePath } = usePath();
  const [nowMs, setNowMs] = useState(Date.now());
  const [index, setIndex] = useState(0);
  // Session mock settles (live/demo pay) — re-read when panel bumps dismiss tick
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readDismissedIds());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setDismissedIds(readDismissedIds());
  }, [liveView, rightBarDismissTick]);

  const alerts = useMemo(
    () => collectCheckoutChargeAlerts(rooms, nowMs, dismissedIds),
    [rooms, nowMs, dismissedIds],
  );

  useEffect(() => {
    if (index >= alerts.length) setIndex(0);
  }, [alerts.length, index]);

  const current = alerts[Math.min(index, Math.max(alerts.length - 1, 0))];
  // Paid / real properties: no top-strip alert (try-live demo only)
  const open = Boolean(
    liveView
    && !isHomePath
    && canHandleRoomCharge(permission)
    && current,
  );

  const openPanel = () => {
    if (!current) return;
    dispatch(bumpRightBarDismiss());
    dispatch(setLayoutRoomChargePanel(null));
    window.setTimeout(() => {
      // Alert pay path: confirm UI runs, but settle is session-mock only
      dispatch(setLayoutRoomChargePanel({
        roomId: current.roomId,
        bookingId: current.bookingId,
        mockSessionSettle: true,
      }));
    }, 260);
  };

  const viewAllCharges = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem('book_search', 'room charge');
    history.push({
      pathname: HOSPITALITY_PATHS.reservations,
      state: { bookSearch: 'room charge' },
    });
  };

  /** Opens room-charge panel (pay flow). Live/demo settle is mocked inside the panel. */
  const onPayCharge = (e: React.MouseEvent) => {
    e.stopPropagation();
    logButtonClick('Pay charge', { source: 'checkout_room_charge_alert' });
    openPanel();
  };

  return (
    <LayoutAlertPresence open={open}>
      {current ? (
        <S.Container>
          <S.WarnIcon>
            <Attention fontSize="medium" />
          </S.WarnIcon>
          <S.Body>
            <S.Title>
              {`Unsettled room charge — checkout ${formatDueLabel(current.endDateMs, nowMs).toLowerCase()}`}
            </S.Title>
            <S.Line>
              <S.Chip>
                <MeetingRoom sx={{ fontSize: 16 }} />
                <S.ChipText>{current.roomName}</S.ChipText>
              </S.Chip>
              <S.Chip>
                <Person sx={{ fontSize: 16 }} />
                <S.ChipText>{current.bookerName}</S.ChipText>
              </S.Chip>
              <S.Chip accent>
                <S.ChipText>
                  {currency}
                  {current.amount.toLocaleString()}
                </S.ChipText>
              </S.Chip>
              {(current.description || current.branch) ? (
                <S.Chip>
                  <AccountTree sx={{ fontSize: 16 }} />
                  <S.ChipText title={[current.branch, current.description].filter(Boolean).join(' · ')}>
                    {[
                      current.branch,
                      current.description ? truncateText(current.description) : null,
                    ].filter(Boolean).join(' · ')}
                  </S.ChipText>
                </S.Chip>
              ) : null}
            </S.Line>
          </S.Body>
          <S.Actions>
            <S.DismissBtn type="button" onClick={onPayCharge} aria-label="Pay room charge">
              <Payments fontSize="small" />
              Pay charge
            </S.DismissBtn>
            <S.ViewAllBtn type="button" onClick={viewAllCharges}>
              View all charges
            </S.ViewAllBtn>
            {alerts.length > 1 && (
              <S.Nav>
                <S.NavBtn
                  type="button"
                  aria-label="Previous room charge alert"
                  onClick={() => setIndex((i) => (i - 1 + alerts.length) % alerts.length)}
                >
                  <ChevronLeft fontSize="small" />
                </S.NavBtn>
                <S.Count>
                  {Math.min(index, alerts.length - 1) + 1}
                  /
                  {alerts.length}
                </S.Count>
                <S.NavBtn
                  type="button"
                  aria-label="Next room charge alert"
                  onClick={() => setIndex((i) => (i + 1) % alerts.length)}
                >
                  <ChevronRight fontSize="small" />
                </S.NavBtn>
              </S.Nav>
            )}
          </S.Actions>
        </S.Container>
      ) : null}
    </LayoutAlertPresence>
  );
};

export default CheckoutRoomChargeAlert;
